import { DatePicker, Divider, Flex, Table } from 'antd'
import { useState } from 'react'

import { usePersonsServiceGetListPersonTransferRequestEndpoint } from 'src/api/queries'
import {
  PersonTransferRequestSortBy,
  PersonTransferRequestStatus
} from 'src/api/requests'
import { PageBreadcrumbs, SearchBar, Select } from 'src/components'
import { useDefaultSortingItem, useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { ActionModal } from '../components'
import { useTransferColumns, useTransferConfig } from '../utils'

export const TransfersPage = () => {
  const { t } = useLocales()
  const pagination = useTablePagination()

  const { userData } = useUser()
  const { defaultSortingOption } = useDefaultSortingItem()

  const [search, setSearch] = useState<string>()
  const [date, setDate] = useState<string[]>([])
  const [status, setStatus] = useState<PersonTransferRequestStatus>()
  const [ordering, setOrdering] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionId: userData?.currentLegalEntityId,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as PersonTransferRequestSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(status && { status }),
    ...(date?.[0] &&
      date?.[1] && {
        personBirthDateFrom: date?.[0],
        personBirthDateTo: date?.[1]
      })
  }

  const { data, isLoading } =
    usePersonsServiceGetListPersonTransferRequestEndpoint(queryParams)

  const [modalState, setModalState] = useState<{
    type: 'approve' | 'reject' | null
    id?: number
  }>({ type: null })

  const { options: orderingOptions } = useTransferConfig()
  const { columns } = useTransferColumns({ setModalState })

  const onCancel = () => {
    setModalState({ type: null, id: undefined })
  }

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const statusOptions = [
    defaultSortingOption,
    ...Object.values(PersonTransferRequestStatus).map((status) => ({
      value: status,
      label: t(`transfer.${status}`)
    }))
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.transfers')}
      items={[{ title: t('menu.transfers') }]}
    >
      <Flex justify="space-between">
        <Flex gap={8} className="full-width">
          <SearchBar
            className="full-width"
            placeholder={t('transfer.search_placeholder')}
            onSearch={onSearch}
          />
          <Select
            options={statusOptions}
            onChange={setStatus}
            placeholder={t('form.status')}
          />

          <DatePicker.RangePicker
            className="full-width"
            format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
            onChange={(_, datesString) => setDate(datesString)}
            placeholder={[
              t('persons.from_date_of_birth'),
              t('persons.to_date_of_birth')
            ]}
          />

          <Select
            options={orderingOptions}
            onSelect={(opt) => setOrdering(opt)}
            placeholder={t('common.sort_by')}
          />
        </Flex>
      </Flex>

      <Divider />
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
        scroll={{ x: 'max-content' }}
      />

      {modalState?.id && (
        <ActionModal
          id={modalState?.id}
          type={modalState.type}
          onCancel={onCancel}
        />
      )}
    </PageBreadcrumbs>
  )
}
